import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { onAuthStateChanged } from 'firebase/auth'
import { firebaseAuth } from './../../../api/FirebaseAuth'
import SidebarSlider from '../../shared/SidebarSlider'
import LiveLeaderboardRow from './LiveLeaderboardRow.component'
import LiveLeaderboardMobileBar from './LiveLeaderboardMobileBar.component'
import LiveLeaderboardService from '../../../shared/services/LiveLeaderboard.service'
import EchoService from '../../../shared/services/Echo.service'
import SystemSettingStore from '../../../shared/stores/systemSetting.store'
import UserProfileService from '../../../shared/services/UserProfile.service'
import ProductScout from '../../productScout/ProductScout.component'
import './LiveLeaderboardSidebar.component.scss'

// ProductScout is gated separately from the Live Leaderboard. The Live feed
// is shown to all non-blocked roles; ProductScout is only available to users
// whose id appears in this system setting (JSON array).
const PRODUCT_SCOUT_PERMITTED_USERS_KEY = 'product-scout---permitted-users'

/** localStorage key for the currently-selected tab inside the slider panel. */
const TAB_KEY = 'usabg:sidebarTab'

/** Tab identifiers — extend by adding to the list and the `renderTabContent` switch. */
const TABS = {
  LIVE: 'live',
  PRODUCT_SCOUT: 'productScout',
}

const PERSIST_KEY = 'usabg:liveLeaderboard:collapsed'
const MAX_ROWS = 20 // live cap (top of feed, capped on broadcasts)
const HISTORY_PAGE_SIZE = 20 // rows fetched per "Show earlier" click
// How long a freshly-arrived row keeps the `is-new` highlight class.
const NEW_ROW_HIGHLIGHT_MS = 2000
// Roles hidden from the LL widget. Passed to SidebarSlider's role gate via
// the `blockedRoles` prop (UserProfileService.isA contract).
const BLOCKED_ROLES = ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent']

/**
 * Returns "Today" / "Yesterday" / "MMM D" (or "MMM D, YYYY" if not current year).
 * Uses local timezone semantics — `new Date(iso)` parses ISO into local.
 */
function formatDateLabel(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  const opts = { month: 'short', day: 'numeric' }
  if (d.getFullYear() !== today.getFullYear()) opts.year = 'numeric'
  return d.toLocaleDateString(undefined, opts)
}

// Loading / error / empty / ready — drives the panel body's display branch.
const STATUS = {
  LOADING: 'loading',
  ERROR: 'error',
  EMPTY: 'empty',
  READY: 'ready',
}

/**
 * Walk the rows array and tag each row as `anchor` (first in a consecutive
 * group) or `continuation`. Grouping is by `av_group_id` — sales submitted
 * together as one customer transaction (multiple coverages, one submission)
 * share an av_group_id and render as anchor + continuation rows. Rows with
 * a null/missing av_group_id never group (each becomes its own anchor).
 *
 * Within each multi-entry group (size >= 2), rows are sorted by `av_points`
 * DESC so the highest-value policy becomes the anchor. Tiebreaker falls
 * through to original order (Array.sort is stable). Single-entry groups
 * pass through untouched. Cross-group order is preserved.
 *
 * Every entry carries:
 *   - `streakCount`: total length of the group
 *   - `runIndex`: 1-based position within the group (1 = anchor, 2+ = continuation)
 */
function decorateGroups(rows) {
  const out = []
  let buffer = [] // rows accumulated for the current group
  let bufferGroupId = null

  const flush = () => {
    if (buffer.length === 0) return
    let groupRows = buffer
    if (groupRows.length >= 2) {
      groupRows = [...groupRows].sort((a, b) => (Number(b.av_points) || 0) - (Number(a.av_points) || 0))
    }
    const total = groupRows.reduce((sum, r) => sum + (Number(r.av_points) || 0), 0)
    const streakCount = groupRows.length
    groupRows.forEach((row, idx) => {
      out.push({
        row,
        variant: idx === 0 ? 'anchor' : 'continuation',
        streakCount,
        runIndex: idx + 1,
        groupTotalPoints: total,
      })
    })
    buffer = []
    bufferGroupId = null
  }

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const currId = row.av_group_id
    // Group only when both this row and the current buffer share the SAME
    // truthy av_group_id. Null/missing ids always start a new group.
    const sameGroup = currId && currId === bufferGroupId
    if (!sameGroup) {
      flush()
      bufferGroupId = currId || null
    }
    buffer.push(row)
  }
  flush()
  return out
}

/**
 * `LiveLeaderboardSidebar` — the Live Sales feature inside the generic
 * `SidebarSlider` shell. Owns the Reverb subscription, REST hydration, row
 * state + grouping, pagination, and status placeholders. Delegates the
 * off-canvas chrome, route + role gating, collapsed-state persistence, and
 * mobile-slot positioning to `SidebarSlider`.
 *
 * @param {Object} props
 * @param {string[]} [props.disabledRoutes=[]] - Route prefixes where the
 *   slider must not render. Passed through to `SidebarSlider`.
 * @param {string[]} [props.enabledRoutes=[]] - Route prefixes where the
 *   slider should render regardless of the default auth-route rule.
 */
function LiveLeaderboardSidebarInner({ disabledRoutes = [], enabledRoutes = [], isProductScoutPermitted = false }) {
  const [rows, setRows] = useState([]) // live: latest MAX_ROWS, prepended on broadcasts
  const [historyRows, setHistoryRows] = useState([]) // paginated older sales, appended on Show More
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyExhausted, setHistoryExhausted] = useState(false)
  const [nowTick, setNowTick] = useState(Date.now())
  const [status, setStatus] = useState(STATUS.LOADING)
  const [connection, setConnection] = useState('connecting') // 'connecting' | 'connected' | 'degraded'
  const [newIds, setNewIds] = useState(new Set())
  // Active tab inside the slider's panel. Persisted across reloads so the
  // user lands on whichever tab they last engaged with. Default 'live' so the
  // Live feed shows on first-time visitors (matches the handle's "Live"
  // label + pulse semantics).
  const [activeTab, setActiveTab] = useState(() => {
    const stored = localStorage.getItem(TAB_KEY)
    // Only honor a persisted ProductScout tab if the viewer is currently
    // permitted to see it — otherwise fall back to Live so they don't end up
    // on a hidden tab.
    return stored === TABS.PRODUCT_SCOUT && isProductScoutPermitted ? TABS.PRODUCT_SCOUT : TABS.LIVE
  })
  // If permission flips to false mid-session (e.g. setting was hot-changed),
  // pull the user back to Live so the panel never renders a hidden tab.
  useEffect(() => {
    if (!isProductScoutPermitted && activeTab === TABS.PRODUCT_SCOUT) {
      setActiveTab(TABS.LIVE)
    }
  }, [isProductScoutPermitted, activeTab])
  useEffect(() => {
    localStorage.setItem(TAB_KEY, activeTab)
  }, [activeTab])
  const bufferRef = useRef([])
  const hydratedRef = useRef(false)
  const channelRef = useRef(null)
  const hasConnectedOnce = useRef(false)

  // Reactive auth gate. Firebase restores the session ASYNCHRONOUSLY, so a one-time
  // `currentUser` snapshot at mount would be null for a beat on every load and the
  // feed would then never start for a logged-in user. Drive off onAuthStateChanged
  // (fires on sign-in/out, NOT on token refresh) so the effect below re-runs exactly
  // when auth resolves or clears — no unauthenticated live-feed / broadcasting-auth.
  const [authUid, setAuthUid] = useState(() => (firebaseAuth.currentUser || {}).uid || null)
  useEffect(() => onAuthStateChanged(firebaseAuth, u => setAuthUid((u && u.uid) || null)), [])

  // Subscribe → buffer → hydrate → drain
  useEffect(() => {
    // Auth gate: no live-feed fetch and no Echo/broadcasting-auth socket unless a
    // user is signed in. On sign-out (or the pre-restore window) tear the socket down.
    if (!authUid) {
      EchoService.disconnect()
      return
    }
    let mounted = true

    const flagAsNew = id => {
      setNewIds(prev => {
        const next = new Set(prev)
        next.add(id)
        return next
      })
      setTimeout(() => {
        if (!mounted) return
        setNewIds(prev => {
          if (!prev.has(id)) return prev
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      }, NEW_ROW_HIGHLIGHT_MS)
    }

    const applyEvent = evt => {
      const id = evt.av_id
      if (evt.action === 'removed') {
        // Remove from both arrays — broadcasts only fire for in-cache rows,
        // but removing from history (if present) is a safe no-op when not.
        setRows(prev => prev.filter(r => r.av_id !== id))
        setHistoryRows(prev => prev.filter(r => r.av_id !== id))
        return
      }
      if (!evt.item) return
      setRows(prev => {
        const next = prev.filter(r => r.av_id !== id)
        next.unshift(evt.item)
        if (evt.action === 'posted') flagAsNew(id)
        return next.slice(0, MAX_ROWS)
      })
      // If this row was in history (rare race), drop it — the live copy is canonical.
      setHistoryRows(prev => prev.filter(r => r.av_id !== id))
    }

    const handler = evt => {
      if (!hydratedRef.current) {
        bufferRef.current.push(evt)
        return
      }
      applyEvent(evt)
    }

    const hydrate = async () => {
      try {
        const res = await LiveLeaderboardService.getLatest(MAX_ROWS)
        if (!mounted) return
        // automation-api-client unwraps response.data.payload — rows arrive directly.
        // On error you get { error, status, fields } back; guard with Array.isArray.
        if (!Array.isArray(res)) {
          setStatus(STATUS.ERROR)
          hydratedRef.current = true
          return
        }
        const merged = [...res]
        bufferRef.current.forEach(evt => {
          if (evt.action === 'removed') {
            const idx = merged.findIndex(r => r.av_id === evt.av_id)
            if (idx >= 0) merged.splice(idx, 1)
            return
          }
          if (!evt.item) return
          const idx = merged.findIndex(r => r.av_id === evt.av_id)
          if (idx >= 0) merged.splice(idx, 1)
          merged.unshift(evt.item)
        })
        const finalRows = merged.slice(0, MAX_ROWS)
        setRows(finalRows)
        setStatus(finalRows.length === 0 ? STATUS.EMPTY : STATUS.READY)
        bufferRef.current = []
        hydratedRef.current = true
      } catch (e) {
        console.error('[LiveLeaderboard] hydration failed', e)
        setStatus(STATUS.ERROR)
        hydratedRef.current = true
      }
    }

    EchoService.init()
      .then(echo => {
        if (!mounted) return
        channelRef.current = echo.private('leaderboards.live').listen('.feed.changed', handler)

        const conn = echo.connector?.pusher?.connection
        if (conn) {
          // Seed React state from the pusher's current state. Critical when the
          // sidebar remounts (e.g. on route change) against an already-connected
          // singleton — the 'connected' event won't re-fire for state that was
          // already true at the time of bind, so without this seed the indicator
          // would stay stuck on 'connecting' forever.
          const STATE_MAP = {
            connected: 'connected',
            connecting: 'connecting',
            initialized: 'connecting',
            unavailable: 'degraded',
            failed: 'degraded',
            disconnected: 'degraded',
          }
          setConnection(STATE_MAP[conn.state] || 'connecting')

          // If we mounted on an already-connected pusher, treat the first future
          // 'connected' fire as a true reconnect (and re-hydrate accordingly).
          if (conn.state === 'connected') {
            hasConnectedOnce.current = true
          }

          conn.bind('connected', () => {
            if (!mounted) return
            setConnection('connected')
            // P3-1: Skip duplicate hydrate on first connect — the explicit hydrate()
            // below already runs. Subsequent 'connected' fires are reconnects.
            if (!hasConnectedOnce.current) {
              hasConnectedOnce.current = true
              return
            }
            hydratedRef.current = false
            bufferRef.current = []
            setStatus(STATUS.LOADING)
            hydrate()
          })
          conn.bind('disconnected', () => mounted && setConnection('degraded'))
          conn.bind('unavailable', () => mounted && setConnection('degraded'))
          conn.bind('failed', () => mounted && setConnection('degraded'))
        }

        hydrate()
      })
      .catch(err => {
        // If Echo can't initialize (missing env, Reverb down), still hydrate via REST
        // so the widget shows historical rows. We just won't get real-time updates.
        console.warn('[LiveLeaderboard] Echo init failed; proceeding with REST-only hydration', err)
        if (mounted) {
          setConnection('degraded')
          hydrate()
        }
      })

    return () => {
      mounted = false
      if (window.Echo) {
        try {
          window.Echo.leaveChannel('private-leaderboards.live')
        } catch (e) {
          /* no-op */
        }
      }
    }
  }, [authUid])

  // Single shared 30s tick for relative-time labels
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000)
    return () => clearInterval(id)
  }, [])

  const retry = () => {
    hydratedRef.current = false
    setStatus(STATUS.LOADING)
    setHistoryRows([])
    setHistoryExhausted(false)
    LiveLeaderboardService.getLatest(MAX_ROWS).then(res => {
      if (!Array.isArray(res)) {
        setStatus(STATUS.ERROR)
        return
      }
      setRows(res)
      setStatus(res.length === 0 ? STATUS.EMPTY : STATUS.READY)
      hydratedRef.current = true
    })
  }

  // Load older sales, cursor-paginated. Cursor = av_id of the bottom-most loaded
  // row (history if present, else the live tail). Stops when a chunk returns
  // fewer than HISTORY_PAGE_SIZE rows.
  const loadMore = async () => {
    if (historyLoading || historyExhausted) return
    const cursorRow = historyRows.length > 0 ? historyRows[historyRows.length - 1] : rows[rows.length - 1]
    if (!cursorRow) return
    setHistoryLoading(true)
    try {
      const res = await LiveLeaderboardService.getOlderThan(cursorRow.av_id, HISTORY_PAGE_SIZE)
      if (Array.isArray(res)) {
        // Dedup against everything we already have (live + history)
        const existing = new Set([...rows, ...historyRows].map(r => r.av_id))
        const fresh = res.filter(r => !existing.has(r.av_id))
        setHistoryRows(prev => [...prev, ...fresh])
        if (res.length < HISTORY_PAGE_SIZE) setHistoryExhausted(true)
      }
    } catch (e) {
      console.error('[LiveLeaderboard] loadMore failed', e)
    } finally {
      setHistoryLoading(false)
    }
  }

  const renderBody = () => {
    if (status === STATUS.LOADING) {
      return (
        <ul className='LiveLeaderboardSidebar__list LiveLeaderboardSidebar__list--loading'>
          {[0, 1, 2, 3, 4].map(i => (
            <li key={i} className='LiveLeaderboardRow LiveLeaderboardRow--skeleton'>
              <div className='LiveLeaderboardRow__headshot skeleton-circle' />
              <div className='LiveLeaderboardRow__body'>
                <div className='skeleton-bar skeleton-bar--md' />
                <div className='skeleton-bar skeleton-bar--sm' />
                <div className='skeleton-bar skeleton-bar--xs' />
              </div>
            </li>
          ))}
        </ul>
      )
    }
    if (status === STATUS.ERROR) {
      return (
        <div className='LiveLeaderboardSidebar__state'>
          <p>Couldn't load the live feed.</p>
          <button onClick={retry} className='LiveLeaderboardSidebar__retry'>
            Retry
          </button>
        </div>
      )
    }
    if (status === STATUS.EMPTY) {
      return (
        <div className='LiveLeaderboardSidebar__state'>
          <p>No recent sales yet.</p>
          <small>New sales appear here automatically.</small>
        </div>
      )
    }
    // Merge live + history with dedup (live wins on conflicts), then group
    // consecutive same-agent rows. First occurrence in a run is an "anchor"
    // (full row with headshot/name/time + 🔥 if streak); subsequent rows are
    // "continuation" (compact, multiplier badge). Date dividers are inserted
    // when consecutive rows fall on different calendar days so users can scan
    // straight to "yesterday's first sale".
    const liveIds = new Set(rows.map(r => r.av_id))
    const allRows = [...rows, ...historyRows.filter(r => !liveIds.has(r.av_id))]
    const grouped = decorateGroups(allRows)

    let lastDateLabel = null
    const items = []
    grouped.forEach(({ row, variant, streakCount, runIndex, groupTotalPoints }) => {
      const dateLabel = formatDateLabel(row.av_created)
      if (dateLabel && dateLabel !== lastDateLabel) {
        items.push(
          <li
            key={`date-${row.av_id}`}
            className='LiveLeaderboardSidebar__date-divider'
            role='separator'
            aria-label={dateLabel}
          >
            {dateLabel}
          </li>
        )
        lastDateLabel = dateLabel
      }
      items.push(
        <LiveLeaderboardRow
          key={row.av_id}
          row={row}
          nowTick={nowTick}
          isNew={newIds.has(row.av_id)}
          variant={variant}
          streakCount={streakCount}
          runIndex={runIndex}
          groupTotalPoints={groupTotalPoints}
        />
      )
    })

    if (!historyExhausted && allRows.length > 0) {
      items.push(
        <li key='load-more' className='LiveLeaderboardSidebar__load-more'>
          <button
            type='button'
            onClick={loadMore}
            disabled={historyLoading}
            className='LiveLeaderboardSidebar__load-more-btn'
          >
            {historyLoading ? 'Loading…' : 'Show earlier sales'}
          </button>
        </li>
      )
    } else if (historyExhausted) {
      items.push(
        <li key='end-of-feed' className='LiveLeaderboardSidebar__end-of-feed' aria-label='End of feed'>
          End of feed
        </li>
      )
    }

    return <ul className='LiveLeaderboardSidebar__list'>{items}</ul>
  }

  const firstRow = rows.length > 0 ? rows[0] : null

  return (
    <SidebarSlider
      id='liveLeaderboard'
      persistKey={PERSIST_KEY}
      side='right'
      handleLabel='Live'
      headerTitle={
        <SidebarTabBar
          activeTab={activeTab}
          onChange={setActiveTab}
          isProductScoutPermitted={isProductScoutPermitted}
        />
      }
      ariaLabel='Live Sales Feed'
      disabledRoutes={disabledRoutes}
      enabledRoutes={enabledRoutes}
      blockedRoles={BLOCKED_ROLES}
      bodyClass='has-live-leaderboard-mobile-bar'
      pulseState={connection}
      mobileBar={
        <LiveLeaderboardMobileBar
          row={firstRow}
          nowTick={nowTick}
          connection={connection}
          isNew={firstRow ? newIds.has(firstRow.av_id) : false}
          status={status}
        />
      }
    >
      {activeTab === TABS.PRODUCT_SCOUT && isProductScoutPermitted ? <ProductScout /> : renderBody()}
    </SidebarSlider>
  )
}

/**
 * Tab bar rendered at the top of the slider's panel. Hosts the toggle
 * between the Live feed and the ProductScout AI assistant. The slider's
 * handle and connection-pulse indicator always reflect the "Live" feed
 * regardless of which tab is active (the live data is what justifies the
 * always-visible pulse).
 *
 * Sticky-positioned so it stays visible above scrollable tab content.
 */
function SidebarTabBar({ activeTab, onChange, isProductScoutPermitted }) {
  const tabs = [
    { id: TABS.LIVE, label: 'Live Leaderboard' },
    isProductScoutPermitted ? { id: TABS.PRODUCT_SCOUT, label: 'ProductScout' } : null,
  ].filter(Boolean)
  return (
    <nav className='LiveLeaderboardSidebar__tabs' role='tablist' aria-label='Sidebar content selector'>
      {tabs.map(t => {
        const isActive = activeTab === t.id
        return (
          <button
            key={t.id}
            type='button'
            role='tab'
            aria-selected={isActive}
            className={`LiveLeaderboardSidebar__tab${isActive ? ' is-active' : ''}`}
            onClick={() => onChange(t.id)}
          >
            {t.label}
          </button>
        )
      })}
    </nav>
  )
}

// The sidebar itself is always rendered (subject to route + role gates inside
// SidebarSlider). The ProductScout tab is gated separately: visible only when
// the current user's id appears in the `product-scout---permitted-users`
// system setting (JSON array).
//
// Read `SystemSettingStore.Settings[…]` directly (not via .get()) so the
// observer wrapper picks up the change when the store first hydrates from the
// API. When the setting is missing/empty/not-yet-loaded, the array is empty
// and nobody passes — fail-closed by design.
const LiveLeaderboardSidebar = observer(props => {
  const userId = UserProfileService.getUserId()
  const permitted = SystemSettingStore.Settings[PRODUCT_SCOUT_PERMITTED_USERS_KEY]
  const ids = Array.isArray(permitted) ? permitted : []
  const isProductScoutPermitted = !!userId && userId > 0 && ids.some(id => Number(id) === Number(userId))
  return <LiveLeaderboardSidebarInner {...props} isProductScoutPermitted={isProductScoutPermitted} />
})

export default LiveLeaderboardSidebar
